import { Platform } from "react-native";
/** A play tracks lifecycle and position only. Credit belongs to the server. */
export type PlayContext = {
  song_id: string;
  device_id: string;
  platform: "ios" | "android" | "web";
  collection: string | null;
  country_code?: string;
  queueItemId: string;
};
export type StopReason =
  "track_change" | "queue_end" | "pause" | "unload" | "user";
export type PlayCredentials = {
  playback_id: string;
  token: string;
  interval_seconds: number;
  last_seq: number;
};
export type PlaySink = {
  start: (context: PlayContext, position?: number) => string;
  heartbeat: (
    id: string,
    seq: number,
    position: number,
    seeked?: boolean,
  ) => void;
  stop: (
    id: string,
    seq: number,
    position: number,
    reason: StopReason,
    seeked?: boolean,
  ) => void;
};

export function createPlaySessionTracker(sink: PlaySink) {
  let context: PlayContext | null = null;
  let id: string | null = null;
  let playing = false;
  let pausedPlay = false;
  let position = 0;
  let seq = 0;
  let seeked = false;
  let intervalSeconds = 30; // Server default until an offline start can be sent.
  let timer: ReturnType<typeof setTimeout> | undefined;
  let lastSent = 0;

  function clearTimer() {
    clearTimeout(timer);
    timer = undefined;
  }
  function arm() {
    clearTimer();
    if (!playing) return;
    timer = setTimeout(
      () => {
        if (!id) start();
        if (id && playing) {
          lastSent = performance.now();
          if (seeked) sink.heartbeat(id, ++seq, position, true);
          else sink.heartbeat(id, ++seq, position);
          seeked = false;
          arm();
        }
      },
      Math.max(0, lastSent + intervalSeconds * 1000 - performance.now()),
    );
  }
  function start() {
    if (id || !playing || !context) return;
    seq = 0;
    pausedPlay = false;
    intervalSeconds = 30;
    seeked = false;
    lastSent = performance.now();
    id = sink.start(context, position);
    arm();
  }
  function stop(reason: StopReason) {
    clearTimer();
    if (!id) return;
    const previous = id;
    if (reason === "pause") {
      if (pausedPlay) return;
      pausedPlay = true;
    } else {
      id = null;
      pausedPlay = false;
    }
    if (seeked) sink.stop(previous, ++seq, position, reason, true);
    else sink.stop(previous, ++seq, position, reason);
    seeked = false;
  }
  return {
    setContext(next: PlayContext | null) {
      if (
        next?.queueItemId !== context?.queueItemId ||
        next?.song_id !== context?.song_id ||
        next?.device_id !== context?.device_id
      ) {
        stop(next ? "track_change" : "queue_end");
        playing = false;
        position = 0;
      }
      context = next;
    },
    setPlayback(audible: boolean, paused = false) {
      // Audible confirmation gates a new play. Once started, user intent owns
      // pause/resume and buffering must not interrupt the heartbeat cadence.
      const nextPlaying = !paused && (audible || id !== null);
      const changed = playing !== nextPlaying;
      playing = nextPlaying;
      if (paused) stop("pause");
      if (!playing) clearTimer();
      else {
        if (id && pausedPlay) {
          pausedPlay = false;
          sink.heartbeat(id, ++seq, position, seeked || undefined);
          seeked = false;
          lastSent = performance.now();
        }
        start();
        if (changed) arm();
      }
    },
    onSeek() {
      seeked = true;
    },
    onProgress(next: number) {
      if (Number.isFinite(next)) position = Math.max(0, next);
    },
    onStarted(localId: string, credentials: PlayCredentials) {
      if (localId !== id) return;
      intervalSeconds = credentials.interval_seconds;
      arm();
    },
    restart(reason: StopReason = "track_change") {
      stop(reason);
      playing = false; // Wait for audible confirmation of the restarted item.
    },
    onPageHide() {
      if (Platform.OS !== "web") return;
      playing = false;
      stop("unload");
    },
    dispose() {
      playing = false;
      stop("user");
    },
  };
}
